import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import AuthService from "@app/services/http/auth.service";
import { storeUser } from "@app/store/auth";
import {
  ACCESS_TOKEN_KEY,
  OtpMethod,
  OtpMethodDetail,
  REFRESH_TOKEN_KEY,
  RoutePath,
  SystemMessage,
  USER_INFO_KEY,
} from "@app/constants";
import { UserData, VerifyEmailCodeResponse, VerifyMfaCode } from "@app/types";
import Button from "@app/components/button";
import StorageService from "@core/services/storage";
import { Images } from "@assets/images";
import "./style.scss";
import VerificationInput from "react-verification-input";
import clsx from "clsx";
import { addToast } from "@app/components/toast/toast.service";
import Radio from "@app/components/radio";

const Otp = () => {
  const [searchParams] = useSearchParams();

  const [code, setCode] = useState("");
  const [isError, setIsError] = useState(false);
  const [isMainMethod, setIsMainMethod] = useState(true);
  const [qrCodeMfa, setQrCodeMfa] = useState<string | null>(null);
  const [valueRadio, setValueRadio] = useState<{
    title: string;
    value: number;
  }>();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!StorageService.getSession(ACCESS_TOKEN_KEY)) {
      navigate(`/${RoutePath.SIGN_IN}`);
    }
  }, []);

  useEffect(() => {
    switch (searchParams.get("method")) {
      case OtpMethod.SMS:
        AuthService.sendSmsCode(
          { type: "verification_code_web" },
          {
            Authorization: `Bearer ${StorageService.getSession(ACCESS_TOKEN_KEY)}`,
          }
        ).then((res) => {
          addToast({ text: res.data.message });
        });
        break;
      case OtpMethod.EMAIL:
        AuthService.sendEmailCode({
          Authorization: `Bearer ${StorageService.getSession(ACCESS_TOKEN_KEY)}`,
        }).then((res) => {
          addToast({ text: res.data.message });
        });
        break;
      case OtpMethod.MFA:
        setIsMainMethod(true);
        break;
      default:
        navigate(`/${RoutePath.SIGN_IN}`);
        break;
    }
  }, [searchParams]);

  const redirectToHome = async (
    data: VerifyEmailCodeResponse | VerifyMfaCode
  ) => {
    const { accessToken, refreshToken, user } = data;

    const userData: UserData = {
      id: user.id,
      username: user.username,
      email: user.email,
      role: user.role,
      fullName: user.fullName,
      thumbnail: user.thumbnail,
    };

    StorageService.setSession(ACCESS_TOKEN_KEY, "");
    StorageService.set(ACCESS_TOKEN_KEY, accessToken);
    StorageService.set(REFRESH_TOKEN_KEY, refreshToken);
    StorageService.setObject(USER_INFO_KEY, userData);
    dispatch(storeUser(user));
    navigate(`/${RoutePath.STATISTICS}`);
  };

  const handleLogin = (value?: string) => {
    switch (searchParams.get("method")) {
      case OtpMethod.SMS:
        AuthService.verifySmsCode(
          { code: value ?? code },
          {
            Authorization: `Bearer ${StorageService.getSession(ACCESS_TOKEN_KEY)}`,
          }
        )
          .then((res) => {
            addToast({
              text: SystemMessage.VERIFY_OTP_SUCCESSFULLY,
            });
            redirectToHome(res.data);
          })
          .catch((err) => {
            addToast({
              text: err.data.message || SystemMessage.VERIFY_OTP_FAILED,
              status: "inValid",
            });
          });
        break;
      case OtpMethod.EMAIL:
        AuthService.verifyEmailCode(
          { code: value ?? code },
          {
            Authorization: `Bearer ${StorageService.getSession(ACCESS_TOKEN_KEY)}`,
          }
        )
          .then((res) => {
            addToast({
              text: SystemMessage.VERIFY_OTP_SUCCESSFULLY,
            });
            redirectToHome(res.data);
          })
          .catch((err) => {
            addToast({
              text: err.data.message || SystemMessage.VERIFY_OTP_FAILED,
              status: "inValid",
            });
          });
        break;
      case OtpMethod.MFA:
        AuthService.verifyMfaCode(value ?? code, false, {
          Authorization: `Bearer ${StorageService.getSession(ACCESS_TOKEN_KEY)}`,
        })
          .then((res) => {
            addToast({
              text: SystemMessage.VERIFY_OTP_SUCCESSFULLY,
            });
            redirectToHome(res.data);
          })
          .catch((err) => {
            addToast({
              text: err.data.message || SystemMessage.VERIFY_OTP_FAILED,
              status: "inValid",
            });
          });
        break;
    }
  };

  const handleAutoVerify = (value: string) => {
    handleLogin(value);
  };

  const handleVerify = () => {
    if (code.length === 6) {
      handleLogin();
    } else {
      setIsError(true);
    }
  };

  const changeMethod = () => {
    setIsMainMethod(false);
    setQrCodeMfa(null);
  };

  const handleContinue = () => {
    setIsMainMethod(true);
    setValueRadio(undefined);

    if (valueRadio?.title) {
      navigate(`/${RoutePath.OTP}?method=${valueRadio.title}`);
    }
  };

  const handleCodeChange = (value: string) => {
    setIsError(false);
    setCode(value);
  };

  const renderMethodAnother = () => {
    return Object.values(OtpMethod)
      .filter((item) => item !== searchParams.get("method"))
      .map((key, idx) => {
        return (
          <Radio
            key={idx}
            className="my-2"
            labelClassName="!text-base"
            label={OtpMethodDetail[key].label}
            value={OtpMethodDetail[key].value}
            onChange={() => {
              setValueRadio({
                title: key,
                value: OtpMethodDetail[key].value,
              });
            }}
            checked={valueRadio?.value === OtpMethodDetail[key].value}
          />
        );
      });
  };

  return (
    <div className="bg-color w-screen h-screen flex items-center justify-center">
      <div className="w-[450px] min-h-[450px] p-10 bg-white rounded-xl flex flex-col gap-5">
        <div>
          <div className="flex justify-center">
            <img
              className="w-28"
              src={Images.LogoText.default}
              alt="Bookas Logo"
            />
          </div>
          <div className="mt-6 font-semibold text-xl text-center neutral-n80 font-[SVN-Gilroy]">
            Xác minh OTP
          </div>
        </div>

        {isMainMethod ? (
          <>
            <div className="flex-1 flex flex-col">
              {qrCodeMfa && (
                <>
                  <div>
                    Step 1: Mở ứng dụng Authenticator và quét mã để thêm mã
                  </div>
                  <div className="flex justify-center">
                    <img
                      src={qrCodeMfa}
                      alt="MFA QR Code"
                      className="w-36 h-36"
                    />
                  </div>
                  <div className="mb-6">
                    Step 2: Nhập mã được xuất hiện trong ứng dụng Authenticator
                  </div>
                </>
              )}
              <div className="flex-1 flex flex-col justify-center">
                <div>
                  <VerificationInput
                    placeholder=""
                    value={code}
                    autoFocus
                    onChange={handleCodeChange}
                    classNames={{
                      container: "mx-auto",
                      character: "character",
                      characterInactive: "character--inactive",
                      characterSelected: "character--selected",
                      characterFilled: "character--filled",
                    }}
                    onComplete={handleAutoVerify}
                  />
                  <div
                    className={clsx(
                      "text-center text-xs error-color hidden mt-1",
                      {
                        "!block": !!isError,
                      }
                    )}
                  >
                    Vui lòng nhập đủ 6 số
                  </div>
                </div>
                <div className="mt-3">
                  <div
                    className="text-center text-[14px] text-blue-600 font-medium cursor-pointer"
                    onClick={changeMethod}
                  >
                    Thử với phương thức khác?
                  </div>
                </div>
              </div>
            </div>
            <Button
              containerClassName="mx-auto"
              label="Xác nhận"
              labelClassName="font-[SVN-Gilroy]"
              width="fit-content"
              size="l"
              className="text-lg font-bold px-11"
              onClick={handleVerify}
            />
          </>
        ) : (
          <>
            <div className="black-30 text-sm text-center black-30 font-[SVN-Gilroy] font-medium">
              Hãy chọn hình thức gửi OTP
            </div>
            <div className="flex-1 flex flex-col justify-center">
              <div className="flex">Chọn hình thức gửi OTP:</div>
              <div>{renderMethodAnother()}</div>
            </div>

            <Button
              containerClassName="mx-auto"
              label="Tiếp tục"
              labelClassName="font-[SVN-Gilroy]"
              width="fit-content"
              size="l"
              className="text-lg font-bold px-11"
              onClick={handleContinue}
              disabled={!valueRadio}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default Otp;
